import { IonPage } from "@ionic/react";
import { StepUpAuthProvider } from "@src/appV2/Auth/LoginSignUp/StepUpAuth/context";
import { ACCOUNT_ROLES_PATH } from "@src/appV2/redesign/Accounts/Roles/paths";
import { TiersContainer } from "@src/appV2/redesign/Tiers/Container";
import { TIERS_PATH } from "@src/appV2/redesign/Tiers/paths";
import { type ReactElement } from "react";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";

import { DeprecatedGlobalAppV1Paths } from "../App/paths";
import { ClipboardOrientationLandingPage } from "../ClipboardOrientation/components/LandingPage";
import { OrientationRescheduled } from "../ClipboardOrientation/components/OrientationRescheduled";
import { CLIPBOARD_ORIENTATION_RESCHEDULED_PATH } from "../ClipboardOrientation/paths";
import { AccountsPage } from "../redesign/Accounts/Page";
import {
  ACCOUNT_UPDATES_PATH,
  ACCOUNTS_DOCUMENT_DETAILS_PATH,
  ACCOUNTS_DOCUMENTS_PATH,
  ACCOUNTS_HCF_DOCUMENT_BY_ID_AND_NAME_PATH,
  DELETE_ACCOUNT_PATH,
  PROFILE_PATH,
} from "../redesign/Accounts/paths";
import { AccountRolesContainer } from "../redesign/Accounts/Roles/Container";
import { useIsAccountPageRedesignEnabled } from "../redesign/Accounts/useIsAccountPageRedesignEnabled";
import { ProfilePage } from "../redesign/Profile/Page";
import { useSkillsAssessmentFeatureFlags } from "../SkillsAssessment/api/useSkillsAssessmentFeatureFlags";
import { SkillsAssessmentMyAccountPage } from "../SkillsAssessment/MyAccountPage";
import { SKILLS_ASSESSMENT_PATH } from "../SkillsAssessment/paths";
import { ClipboardResumePage } from "./ClipboardResume/Page";
import { CLIPBOARD_RESUME_PATH } from "./ClipboardResume/paths";
import { DeleteAccountPage } from "./DeleteAccount";
import { DocumentsPageContainer } from "./Documents/Container";
import { DocumentDetailsPageContainer } from "./Documents/DocumentDetailsPageContainer";
import { PAYROLL_PATH } from "./Payroll/paths";
import { PayrollRoutes } from "./Payroll/Routes";
import { TAX_INFORMATION_PATH, TaxInformationPage } from "./TaxInformation";
import { UpdatesPage } from "./Updates/Page";

export function AccountRoutes(): ReactElement {
  const match = useRouteMatch();
  const { isClipboardOrientationEnabled } = useSkillsAssessmentFeatureFlags();
  const isAccountPageRedesignEnabled = useIsAccountPageRedesignEnabled();

  return (
    <Switch>
      <Route exact path={`${match.url}/${ACCOUNTS_DOCUMENTS_PATH}`}>
        <IonPage>
          <DocumentsPageContainer />
        </IonPage>
      </Route>
      <Route exact path={`${match.url}/${ACCOUNTS_HCF_DOCUMENT_BY_ID_AND_NAME_PATH}`}>
        <IonPage>
          <DocumentsPageContainer />
        </IonPage>
      </Route>
      <Route exact path={`${match.url}/${ACCOUNTS_DOCUMENT_DETAILS_PATH}`}>
        <IonPage>
          <DocumentDetailsPageContainer />
        </IonPage>
      </Route>
      <Route exact path={`${match.url}/${SKILLS_ASSESSMENT_PATH}`}>
        {isClipboardOrientationEnabled ? (
          <ClipboardOrientationLandingPage />
        ) : (
          <SkillsAssessmentMyAccountPage />
        )}
      </Route>
      <Route exact path={`${match.url}/${CLIPBOARD_ORIENTATION_RESCHEDULED_PATH}`}>
        {isClipboardOrientationEnabled ? (
          <OrientationRescheduled />
        ) : (
          <Redirect to={`${match.url}/${SKILLS_ASSESSMENT_PATH}`} />
        )}
      </Route>
      <Route exact path={`${match.url}/${DELETE_ACCOUNT_PATH}`}>
        <StepUpAuthProvider>
          <DeleteAccountPage />
        </StepUpAuthProvider>
      </Route>
      <Route path={`${match.url}/${ACCOUNT_ROLES_PATH}`}>
        <AccountRolesContainer />
      </Route>
      <Route path={`${match.url}/${PAYROLL_PATH}`}>
        <PayrollRoutes />
      </Route>
      <Route exact path={`${match.url}/${CLIPBOARD_RESUME_PATH}`}>
        <IonPage>
          <ClipboardResumePage />
        </IonPage>
      </Route>
      <Route path={`${match.url}/${TIERS_PATH}`}>
        <TiersContainer />
      </Route>
      <Route exact path={`${match.url}/${ACCOUNT_UPDATES_PATH}`}>
        <UpdatesPage />
      </Route>
      <Route exact path={`${match.url}/${TAX_INFORMATION_PATH}`}>
        <TaxInformationPage />
      </Route>

      <Route exact path={`${match.url}/${PROFILE_PATH}`}>
        <ProfilePage />
      </Route>
      <Route>
        {isAccountPageRedesignEnabled ? (
          <AccountsPage />
        ) : (
          <Redirect to={DeprecatedGlobalAppV1Paths.ACCOUNT} />
        )}
      </Route>
    </Switch>
  );
}
